import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import {
  apiBaseUrl,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPostBlob,
  apiPut,
  ApiError,
} from "../api";
import type { ID } from "../id";

/**
 * Season documents: what a leader agrees to at „Registrace do sezóny"
 * (GDPR, soutěžní řád, …). A document is a name plus append-only versions;
 * a consent names a version, so replacing the file asks every club again.
 * Backend: internal/service/seasondoc.
 */

export interface SeasonDocumentVersion {
  id: ID;
  document_id: ID;
  version: number;
  /** Set for a link version; then the file fields are empty. */
  url: string;
  original_name: string;
  content_type: string;
  size_bytes: number;
  created_at: Dayjs;
}

export interface SeasonDocument {
  id: ID;
  season_id: ID;
  name: string;
  sort_order: number;
  current_version_id?: ID;
  retired_at?: Dayjs | null;
  current: SeasonDocumentVersion | null;
  versions: SeasonDocumentVersion[];
  /** Teams that agreed to the CURRENT version. */
  agreed_teams: number;
}

export interface DocumentConsent {
  version_id: ID;
  version: number;
  agreed_at: Dayjs;
  user_name: string;
  impersonator_name?: string;
}

/** One live document of a season as a team sees it. */
export interface TeamSeasonDocument {
  document_id: ID;
  name: string;
  version_id: ID;
  version: number;
  original_name: string;
  /** Set when the version is a link to a page rather than a PDF. */
  url: string;
  /** The team agreed to the CURRENT version. */
  agreed: boolean;
  /** The latest consent to any version; with `agreed` false it is to an
   * older wording, which the workspace reads as „nová verze". */
  consent?: DocumentConsent;
}

/** What a version is made of: an uploaded PDF, or a link to a page. */
export type DocumentSource = { file: File } | { url: string };

/** Posts a source to an endpoint that takes either: the PDF as a raw body
 * (its name in ?filename=), or JSON `{url}`. */
function postSource(path: string, source: DocumentSource, query: string[]): Promise<unknown> {
  if ("url" in source) {
    const q = query.length ? `?${query.join("&")}` : "";
    return apiPost(`${path}${q}`, { url: source.url });
  }
  const q = [...query, `filename=${encodeURIComponent(source.file.name)}`];
  return apiPostBlob(`${path}?${q.join("&")}`, source.file);
}

/** One version, inline: the PDF in the browser's viewer, or, for a link,
 * a redirect to the page. Either way the one URL to open. */
export function documentFileUrl(versionId: ID): string {
  return `${apiBaseUrl}/season-document-versions/${versionId}/file`;
}

export function useSeasonDocuments(seasonId: ID | undefined) {
  return useQuery<SeasonDocument[]>({
    queryKey: ["admin", "seasons", seasonId, "documents"],
    queryFn: async () =>
      (await apiGet<{ data: SeasonDocument[] }>(`/seasons/${seasonId}/documents`)).data,
    enabled: !!seasonId,
  });
}

export function useTeamSeasonDocuments(teamId: ID | undefined, seasonId?: ID) {
  return useQuery<TeamSeasonDocument[]>({
    queryKey: ["teams", teamId, "season-documents", seasonId ?? "active"],
    queryFn: async () => {
      const q = seasonId ? `?season_id=${seasonId}` : "";
      return (
        await apiGet<{ data: TeamSeasonDocument[] }>(
          `/teams/${teamId}/season-registration/documents${q}`,
        )
      ).data;
    },
    enabled: !!teamId,
  });
}

function invalidateDocuments(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ["admin", "seasons"] });
  // The per-document consent lists: a new version or consent changes them.
  qc.invalidateQueries({ queryKey: ["admin", "season-documents"] });
  qc.invalidateQueries({ queryKey: ["teams"] });
}

export function useCreateSeasonDocument(seasonId: ID) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { name: string; source: DocumentSource }>({
    mutationFn: ({ name, source }) =>
      postSource(`/seasons/${seasonId}/documents`, source, [`name=${encodeURIComponent(name)}`]),
    // The dialog renders the refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useReplaceSeasonDocumentFile() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { documentId: ID; source: DocumentSource }>({
    mutationFn: ({ documentId, source }) =>
      postSource(`/season-documents/${documentId}/file`, source, []),
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useRenameSeasonDocument() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { documentId: ID; name: string }>({
    mutationFn: ({ documentId, name }) => apiPatch(`/season-documents/${documentId}`, { name }),
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useReorderSeasonDocuments(seasonId: ID) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID[]>({
    mutationFn: (ids) => apiPut(`/seasons/${seasonId}/documents/order`, { document_ids: ids }),
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useRetireSeasonDocument() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (documentId) => apiDelete(`/season-documents/${documentId}`),
    // Reported inside the confirm dialog.
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateDocuments(qc),
  });
}

/** Agrees to the active season's pending documents without a registration
 * submit: the team page's „Potvrdit souhlas". */
export function useConsentSeasonDocuments(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, ID[]>({
    mutationFn: (versionIds) =>
      apiPost(`/teams/${teamId}/season-registration/documents/consent`, { version_ids: versionIds }),
    // The dialog renders the refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      invalidateDocuments(qc);
      qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] });
    },
    // Refused because the documents moved (one added or replaced since the
    // page loaded): the dialog lists what the server has, or every retry
    // fails over a checkbox that is not there.
    onError: (err) => {
      if (documentsMovedCode(err)) qc.invalidateQueries({ queryKey: ["teams", teamId, "season-documents"] });
    },
  });
}

/** The refusal code when a consent failed because the season's documents
 * changed under the page (seasondoc.ErrChanged / ErrNotAgreed), else null. */
export function documentsMovedCode(err: unknown): "documents_changed" | "documents_not_agreed" | null {
  if (!(err instanceof ApiError)) return null;
  const code = (err.body as { code?: string } | null)?.code;
  return code === "documents_changed" || code === "documents_not_agreed" ? code : null;
}

/** One team's recorded agreement to a version of a document, as the
 * admin's per-document list shows it (seasondoc.TeamConsent). */
export interface TeamDocumentConsent {
  id: ID;
  team_id: ID;
  team_name: string;
  version_id: ID;
  version: number;
  /** The consent names the current version, so it is the one in force. */
  current: boolean;
  agreed_at: Dayjs;
  user_name: string;
  impersonator_name?: string;
}

export function useSeasonDocumentConsents(documentId: ID | undefined) {
  return useQuery<TeamDocumentConsent[]>({
    queryKey: ["admin", "season-documents", documentId, "consents"],
    queryFn: async () =>
      (await apiGet<{ data: TeamDocumentConsent[] }>(`/season-documents/${documentId}/consents`)).data,
    enabled: !!documentId,
  });
}

/** Removes one recorded consent (an admin correction): the document is
 * pending for the team again. */
export function useRevokeDocumentConsent() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (consentId) => apiDelete(`/team-document-consents/${consentId}`),
    // Reported inside the confirm dialog.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      invalidateDocuments(qc);
      qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] });
    },
  });
}
